import React from 'react';
import Alert from '@mui/joy/Alert';
import Avatar from '@mui/joy/Avatar';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import Container from '@mui/joy/Container';
import Dropdown from '@mui/joy/Dropdown';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import IconButton from '@mui/joy/IconButton';
import Input from '@mui/joy/Input';
import Menu from '@mui/joy/Menu';
import MenuButton from '@mui/joy/MenuButton';
import MenuItem from '@mui/joy/MenuItem';
import Stack from '@mui/joy/Stack';
import Tabs from '@mui/joy/Tabs';
import TabList from '@mui/joy/TabList';
import Tab from '@mui/joy/Tab';
import TabPanel from '@mui/joy/TabPanel';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Divider from '@mui/joy/Divider';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import ContactPhoneRoundedIcon from '@mui/icons-material/ContactPhoneRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import PhotoLibraryRoundedIcon from '@mui/icons-material/PhotoLibraryRounded';
import FamilyRestroomRoundedIcon from '@mui/icons-material/FamilyRestroomRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditRoundedIcon from '@mui/icons-material/EditRounded';

import { fetchCongregationMemberProfile, fetchAllAdminMembers, updateAdminMemberPersonal, fetchCongregationMasters, fetchAdminMembersWithActivityStatus } from '../../api';
import { EditPersonalDialog } from '../../components/congregation/EditPersonalDialog';
import { formatISTDate } from '../../utils/date';
import { useSnackbarStore } from '../../store/useSnackbarStore';
import { useTheme } from '@mui/joy/styles';
import { MARITAL_STATUS_OPTIONS, OCCUPATION_OPTIONS, EDUCATION_OPTIONS, getInitiatedWithLabel } from '../../data/congregationOptions';
import type { CongregationMemberProfile, CongregationDevotionalMilestone, CongregationFamilyRelation, CongregationSpiritualMaster } from '../../types/congregation';

const getMaritalStatusLabel = (value?: string | null): string => {
  if (!value) return '—';
  const option = MARITAL_STATUS_OPTIONS.find((opt) => opt.value === value);
  return option?.label ?? value;
};

const getOccupationLabel = (value?: string | null): string => {
  if (!value) return '—';
  const option = OCCUPATION_OPTIONS.find((opt) => opt.value === value);
  return option?.label ?? value;
};

const getEducationLabel = (value?: string | null): string => {
  if (!value) return '—';
  const option = EDUCATION_OPTIONS.find((opt) => opt.value === value);
  return option?.label ?? value;
};

const getSpiritualMasterName = (masterId: number | null | undefined, masters: CongregationSpiritualMaster[]): string => {
  if (!masterId) return '—';
  const master = masters.find((m) => m.id === masterId);
  return master?.name || `Master #${masterId}`;
};

const getPhotoUrl = (photoUrl?: string | null): string | undefined => {
  if (!photoUrl) return undefined;
  // If it's already a full URL, return as is
  if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) {
    return photoUrl;
  }
  // If it's a path, we can't convert it here - it should be a full URL from backend
  return undefined;
};

const AdminDevoteesPage: React.FC = () => {
  const { showSnackbar } = useSnackbarStore();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const heroBackground = isDark
    ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(10, 15, 26, 0.98) 100%)'
    : 'radial-gradient(120% 140% at 8% 0%, rgba(255, 218, 170, 0.2) 0%, rgba(255, 218, 170, 0) 55%), radial-gradient(120% 140% at 95% 5%, rgba(165, 197, 255, 0.2) 0%, rgba(165, 197, 255, 0) 60%), linear-gradient(180deg, rgba(255, 252, 245, 1) 0%, rgba(248, 250, 255, 1) 45%, rgba(244, 246, 252, 1) 100%)';
  const heroOverlay = isDark
    ? 'radial-gradient(120% 120% at 15% -10%, rgba(56, 189, 248, 0.14), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(251, 191, 36, 0.12), transparent 55%)'
    : 'radial-gradient(120% 120% at 15% -10%, rgba(255, 220, 160, 0.45), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(129, 158, 255, 0.28), transparent 55%)';
  const heroTitle = isDark ? 'rgba(248,250,252,0.94)' : 'text.primary';
  const heroBody = isDark ? 'rgba(226,232,240,0.8)' : 'text.secondary';
  const panelWarm = isDark
    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95))'
    : 'linear-gradient(135deg, rgba(255, 237, 213, 0.9), rgba(255, 247, 231, 0.9))';
  const panelCool = isDark
    ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.95))'
    : 'linear-gradient(135deg, rgba(224, 243, 255, 0.8), rgba(241, 249, 255, 0.95))';
  const orbColor = isDark ? 'rgba(56, 189, 248, 0.18)' : 'rgba(255, 214, 153, 0.35)';
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedMentorId, setSelectedMentorId] = React.useState<number | null>(null);
  const [selectedDevoteeId, setSelectedDevoteeId] = React.useState<number | null>(null);
  const [activeTab, setActiveTab] = React.useState<number>(0);
  const [allMembers, setAllMembers] = React.useState<any[]>([]);
  const [mentorList, setMentorList] = React.useState<Array<{ id: number; name: string }>>([]);
  const [loadingMembers, setLoadingMembers] = React.useState(true);
  const [devoteeProfile, setDevoteeProfile] = React.useState<CongregationMemberProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [masters, setMasters] = React.useState<CongregationSpiritualMaster[]>([]);
  const devoteeSummary = React.useMemo(() => {
    const total = allMembers.length;
    const active = allMembers.filter((member) => member.status === 'active').length;
    const suspended = allMembers.filter((member) => member.status === 'suspended').length;
    const inactive = Math.max(total - active - suspended, 0);
    return { total, active, suspended, inactive };
  }, [allMembers]);

  // Fetch all members on initial load
  React.useEffect(() => {
    const fetchAllMembers = async () => {
      try {
        setLoadingMembers(true);
        // Fetch members with sadhana activity status (active/inactive based on last submission)
        const result = await fetchAdminMembersWithActivityStatus();
        const mapped = (result.devotees || []).map((d: any) => ({
          id: d?.id,
          name: d?.name || `Member #${d.id}`,
          email: d?.email ?? null,
          photoUrl: null,
          status: d?.status ?? 'inactive',
          lastSubmissionAt: d?.lastSubmissionAt ?? null,
          mentorId: null,
          mentorName: null,
        })).filter((m: any) => m.id != null);
        setAllMembers(mapped);
        // Auto-select first member
        if (mapped.length > 0 && !selectedDevoteeId) {
          setSelectedDevoteeId(mapped[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch members:', error);
        showSnackbar('Failed to fetch members', 'danger');
        setAllMembers([]);
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchAllMembers();
  }, []);

  // Fetch spiritual masters
  React.useEffect(() => {
    const fetchMasters = async () => {
      try {
        const data = await fetchCongregationMasters();
        setMasters(data.spiritualMasters || []);
      } catch (error) {
        console.error('Failed to fetch spiritual masters:', error);
      }
    };
    
    fetchMasters();
  }, []);

  // Filter members based on search term and mentor
  const devotees = React.useMemo(() => {
    return allMembers.filter((d) => {
      const matchesSearch = d.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(d.id).includes(searchTerm) ||
        (d.email ?? '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesMentor = !selectedMentorId || d.mentorId === selectedMentorId;
      return matchesSearch && matchesMentor;
    });
  }, [allMembers, searchTerm, selectedMentorId]);

  // Fetch profile for selected devotee
  React.useEffect(() => {
    if (!selectedDevoteeId) return;

    const fetchProfile = async () => {
      try {
        setLoadingProfile(true);
        const profile = await fetchCongregationMemberProfile(selectedDevoteeId);
        setDevoteeProfile(profile);
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        showSnackbar('Failed to fetch devotee profile', 'danger');
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, [selectedDevoteeId, showSnackbar]);

  const selectedDevotee = devotees.find((d) => d.id === selectedDevoteeId);

  const handleAssignMentor = async (memberId: number, mentorId: number) => {
    try {
      // This would call the actual API to assign caretaker
      showSnackbar('Caretaker assignment functionality coming soon', 'neutral');
    } catch (error) {
      showSnackbar('Failed to assign caretaker', 'danger');
    }
  };

  const handleSavePersonal = async (data: any) => {
    if (!selectedDevoteeId) return;
    try {
      await updateAdminMemberPersonal(selectedDevoteeId, data);
      showSnackbar('Profile updated successfully', 'success');
      // Refetch profile
      const profile = await fetchCongregationMemberProfile(selectedDevoteeId);
      setDevoteeProfile(profile);
    } catch (error) {
      console.error('Failed to update profile:', error);
      showSnackbar('Failed to update profile', 'danger');
      throw error;
    }
  };

  return (
    <Box sx={{ width: '100%', overflow: 'hidden', maxWidth: '100%' }}>
      <Stack spacing={{ xs: 2.5, md: 3.5 }} sx={{ width: '100%', maxWidth: '100%' }}>
        <Card
          variant="outlined"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 'xl',
            borderColor: 'divider',
            boxShadow: 'lg',
            background: heroBackground,
            width: '100%',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: heroOverlay,
              opacity: 0.9,
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              width: 180,
              height: 180,
              borderRadius: '50%',
              right: { xs: -60, md: -20 },
              top: { xs: 30, md: 10 },
              background: orbColor,
              filter: 'blur(6px)',
              animation: 'devotees-float 8s ease-in-out infinite',
              '@keyframes devotees-float': {
                '0%': { transform: 'translateY(0px)' },
                '50%': { transform: 'translateY(-12px)' },
                '100%': { transform: 'translateY(0px)' },
              },
              pointerEvents: 'none',
            }}
          />
          <CardContent sx={{ position: 'relative', width: '100%', overflow: 'hidden', boxSizing: 'border-box', maxWidth: '100%', px: { xs: 1.5, sm: 2 }, py: { xs: 2, sm: 2.5 } }}>
            <Stack spacing={{ xs: 1.5, md: 2 }} sx={{ width: '100%', maxWidth: '100%' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.5, md: 2 }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}>
                <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <PersonRoundedIcon sx={{ fontSize: 28, color: 'primary.500', flexShrink: 0 }} />
                    <Typography level="h2" sx={{ fontWeight: 800, minWidth: 0, color: heroTitle }}>
                      Devotees
                    </Typography>
                  </Stack>
                  <Typography level="body-sm" sx={{ color: heroBody, maxWidth: 720, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    View detailed profiles, family information, and sadhana context for every devotee.
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
                  <Chip variant="soft" color="primary">
                    {devoteeSummary.total} total
                  </Chip>
                  <Chip variant="soft" color="success">
                    {devoteeSummary.active} active
                  </Chip>
                  <Chip variant="soft" color="warning">
                    {devoteeSummary.inactive} inactive
                  </Chip>
                </Stack>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1.5, md: 2 }} sx={{ width: '100%', maxWidth: '100%' }}>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: 'lg',
                    background: panelWarm,
                    boxShadow: 'sm',
                    minWidth: 0,
                  }}
                >
                  <CardContent>
                    <Typography level="title-sm" sx={{ color: 'text.secondary' }}>
                      Engagement status
                    </Typography>
                    <Typography level="title-lg" sx={{ mt: 0.5 }}>
                      {devoteeSummary.active} active • {devoteeSummary.inactive} inactive
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      {devoteeSummary.suspended} suspended accounts.
                    </Typography>
                  </CardContent>
                </Card>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: 'lg',
                    background: panelCool,
                    boxShadow: 'sm',
                    minWidth: 0,
                  }}
                >
                  <CardContent sx={{ overflow: 'hidden', boxSizing: 'border-box', maxWidth: '100%' }}>
                    <Typography level="title-sm" sx={{ color: 'text.secondary' }}>
                      Filters
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.75, sm: 1 }} sx={{ mt: 1, width: '100%', minWidth: 0, maxWidth: '100%' }}>
                      {mentorList.length > 0 && (
                        <Select
                          placeholder="Filter by caretaker..."
                          value={selectedMentorId}
                          onChange={(_, value) => setSelectedMentorId(value)}
                          size="sm"
                          sx={{ borderRadius: '999px', minWidth: { xs: 0, sm: 160 }, flex: { xs: 1, sm: 0 }, maxWidth: '100%' }}
                        >
                          <Option value={null}>All Caretakers</Option>
                          {mentorList.map((mentor) => (
                            <Option key={mentor.id} value={mentor.id}>
                              {mentor.name}
                            </Option>
                          ))}
                        </Select>
                      )}
                      <Input
                        placeholder="Search devotees..."
                        startDecorator={<SearchRoundedIcon />}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="sm"
                        sx={{ borderRadius: '999px', bgcolor: 'background.level1', minWidth: { xs: 0, sm: 200 }, flex: 1, maxWidth: '100%' }}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Main Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 350px) minmax(0, 1fr)' },
            gap: { xs: 1.5, md: 3 },
            width: '100%',
            maxWidth: '100%',
          }}
        >
          {/* Devotee List Sidebar */}
          <Card variant="outlined" sx={{ borderRadius: 'xl', height: 'fit-content' }}>
            <CardContent sx={{ display: 'grid', gap: 2 }}>
              <Typography level="title-md" sx={{ fontWeight: 700 }}>
                All Devotees ({devotees.length})
              </Typography>

              {/* Member List */}
              {loadingMembers ? (
                <Stack spacing={1}>
                  {[1, 2, 3].map((i) => (
                    <Sheet key={i} variant="soft" sx={{ p: 1.5, borderRadius: 'md' }}>
                      <CircularProgress size="sm" />
                    </Sheet>
                  ))}
                </Stack>
              ) : devotees.length === 0 ? (
                <Alert color="neutral" variant="soft">
                  <Typography level="body-sm">
                    {searchTerm ? 'No devotees match your search' : 'No assigned devotees yet'}
                  </Typography>
                </Alert>
              ) : (
                <Stack spacing={1}>
                  {devotees.map((devotee) =>
                    selectedDevoteeId === devotee.id ? (
                      <Sheet
                        key={devotee.id}
                        variant="soft"
                        color="primary"
                        sx={{
                          p: 1.5,
                          borderRadius: 'lg',
                          border: '1px solid',
                          borderColor: 'primary.softBorder',
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          setSelectedDevoteeId(devotee.id);
                          setActiveTab(0);
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar size="sm" src={getPhotoUrl(devotee.photoUrl)}>
                            {devotee.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              level="body-sm"
                              sx={{
                                fontWeight: 600,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {devotee.name}
                            </Typography>
                            {devotee.email ? (
                              <Typography level="body-xs" sx={{ opacity: 0.8, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {devotee.email}
                              </Typography>
                            ) : null}
                            <Typography level="body-xs" sx={{ opacity: 0.8 }}>
                              Last submission: {devotee.lastSubmissionAt ? formatISTDate(devotee.lastSubmissionAt) : 'Never'}
                            </Typography>
                          </Stack>
                          <Chip
                            size="sm"
                            variant="soft"
                            color={devotee.status === 'ACTIVE' ? 'success' : devotee.status === 'SUSPENDED' ? 'danger' : 'neutral'}
                          >
                            {devotee.status.charAt(0).toUpperCase() + devotee.status.slice(1).toLowerCase()}
                          </Chip>
                        </Stack>
                      </Sheet>
                    ) : (
                      <Box
                        key={devotee.id}
                        sx={{
                          p: 1.5,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          borderRadius: 'lg',
                          '&:hover': {
                            bgcolor: 'background.level1',
                          },
                        }}
                        onClick={() => {
                          setSelectedDevoteeId(devotee.id);
                          setActiveTab(0);
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar size="sm" src={getPhotoUrl(devotee.photoUrl)}>
                            {devotee.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              level="body-sm"
                              sx={{
                                fontWeight: 600,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {devotee.name}
                            </Typography>
                            {devotee.email ? (
                              <Typography level="body-xs" sx={{ opacity: 0.8, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {devotee.email}
                              </Typography>
                            ) : null}
                            <Typography level="body-xs" sx={{ opacity: 0.8 }}>
                              Last submission: {devotee.lastSubmissionAt ? formatISTDate(devotee.lastSubmissionAt) : 'Never'}
                            </Typography>
                          </Stack>
                          <Chip
                            size="sm"
                            variant="soft"
                            color={devotee.status === 'ACTIVE' ? 'success' : devotee.status === 'SUSPENDED' ? 'danger' : 'neutral'}
                          >
                            {devotee.status.charAt(0).toUpperCase() + devotee.status.slice(1).toLowerCase()}
                          </Chip>
                        </Stack>
                      </Box>
                    )
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Devotee Details Panel */}
          <Card variant="outlined" sx={{ borderRadius: 'xl' }}>
            <CardContent sx={{ display: 'grid', gap: 2.5 }}>
              {loadingProfile ? (
                <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
                  <CircularProgress size="md" />
                </Box>
              ) : !selectedDevotee || !devoteeProfile ? (
                <Alert color="neutral" variant="soft">
                  <Typography level="body-sm">
                    Select a devotee from the list to view their details
                  </Typography>
                </Alert>
              ) : (
                <Stack spacing={2}>
                  {/* Header */}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Avatar size="md" sx={{ width: 64, height: 64 }} src={getPhotoUrl(selectedDevotee.photoUrl)}>
                      {selectedDevotee.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Stack spacing={0.5} sx={{ flex: 1 }}>
                      <Typography level="h3" sx={{ fontWeight: 800 }}>
                        {selectedDevotee.name}
                      </Typography>
                      <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                        Member ID: {selectedDevotee.id}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip
                          size="sm"
                          variant="soft"
                          color={selectedDevotee.status === 'ACTIVE' ? 'success' : selectedDevotee.status === 'SUSPENDED' ? 'danger' : 'neutral'}
                        >
                          {selectedDevotee.status.charAt(0).toUpperCase() + selectedDevotee.status.slice(1).toLowerCase()}
                        </Chip>
                        {selectedDevotee.mentorName ? (
                          <Chip size="sm" variant="soft" color="primary">
                            Caretaker: {selectedDevotee.mentorName}
                          </Chip>
                        ) : null}
                      </Stack>
                    </Stack>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="sm"
                      startDecorator={<EditRoundedIcon />}
                      onClick={() => setEditDialogOpen(true)}
                    >
                      Edit Profile
                    </Button>
                  </Stack>

                  {/* Tabs */}
                  <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value as number)}>
                    <TabList
                      disableUnderline
                      sx={{
                        px: 0,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        gap: 0.5,
                      }}
                    >
                      <Tab
                        value={0}
                        sx={{
                          py: 1.25,
                          px: 1.5,
                          fontWeight: 600,
                          '&[aria-selected="true"]': {
                            color: 'primary.main',
                            borderBottom: '2px solid',
                            borderColor: 'primary.main',
                          },
                        }}
                      >
                        <PersonRoundedIcon sx={{ fontSize: 20 }} />
                        Identity
                      </Tab>
                      <Tab
                        value={1}
                        sx={{
                          py: 1.25,
                          px: 1.5,
                          fontWeight: 600,
                          '&[aria-selected="true"]': {
                            color: 'primary.main',
                            borderBottom: '2px solid',
                            borderColor: 'primary.main',
                          },
                        }}
                      >
                        <ContactPhoneRoundedIcon sx={{ fontSize: 20 }} />
                        Contact
                      </Tab>
                      <Tab
                        value={2}
                        sx={{
                          py: 1.25,
                          px: 1.5,
                          fontWeight: 600,
                          '&[aria-selected="true"]': {
                            color: 'primary.main',
                            borderBottom: '2px solid',
                            borderColor: 'primary.main',
                          },
                        }}
                      >
                        <TravelExploreRoundedIcon sx={{ fontSize: 20 }} />
                        Address
                      </Tab>
                      <Tab
                        value={3}
                        sx={{
                          py: 1.25,
                          px: 1.5,
                          fontWeight: 600,
                          '&[aria-selected="true"]': {
                            color: 'primary.main',
                            borderBottom: '2px solid',
                            borderColor: 'primary.main',
                          },
                        }}
                      >
                        <PhotoLibraryRoundedIcon sx={{ fontSize: 20 }} />
                        Photos
                      </Tab>
                      <Tab
                        value={4}
                        sx={{
                          py: 1.25,
                          px: 1.5,
                          fontWeight: 600,
                          '&[aria-selected="true"]': {
                            color: 'primary.main',
                            borderBottom: '2px solid',
                            borderColor: 'primary.main',
                          },
                        }}
                      >
                        <FamilyRestroomRoundedIcon sx={{ fontSize: 20 }} />
                        Family
                      </Tab>
                    </TabList>

                    {/* Identity Tab */}
                    <TabPanel value={0} sx={{ p: 0, pt: 2 }}>
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            p: { xs: 1.5, sm: 2 },
                            borderRadius: 'lg',
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.level1',
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 2,
                          }}
                        >
                          <Box>
                            <Typography level="body-xs" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Preferred name
                            </Typography>
                            <Typography level="body-sm" sx={{ color: 'text.primary', fontWeight: 500 }}>
                              {devoteeProfile.member.displayName || '—'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600, mb: 0.5 }}>
                              Congregation ID
                            </Typography>
                            <Typography level="body-sm">#{devoteeProfile.member.id}</Typography>
                          </Box>
                          <Box>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600, mb: 0.5 }}>
                              Legal name
                            </Typography>
                            <Typography level="body-sm">{devoteeProfile.member.legalName || '—'}</Typography>
                          </Box>
                          <Box>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600, mb: 0.5 }}>
                              Phone
                            </Typography>
                            <Typography level="body-sm">{devoteeProfile.member.phone || '—'}</Typography>
                          </Box>
                          <Box>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600, mb: 0.5 }}>
                              Email
                            </Typography>
                            <Typography level="body-sm">{devoteeProfile.member.email || '—'}</Typography>
                          </Box>
                          <Box>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600, mb: 0.5 }}>
                              Gender
                            </Typography>
                            <Typography level="body-sm">{devoteeProfile.member.gender || '—'}</Typography>
                          </Box>
                          <Box>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600, mb: 0.5 }}>
                              Date of birth
                            </Typography>
                            <Typography level="body-sm">
                              {devoteeProfile.member.dateOfBirth ? formatISTDate(devoteeProfile.member.dateOfBirth) : '—'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600, mb: 0.5 }}>
                              Marital status
                            </Typography>
                            <Typography level="body-sm">{getMaritalStatusLabel(devoteeProfile.member.maritalStatus)}</Typography>
                          </Box>
                          <Box>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600, mb: 0.5 }}>
                              Occupation
                            </Typography>
                            <Typography level="body-sm">{getOccupationLabel(devoteeProfile.member.occupation)}</Typography>
                          </Box>
                          <Box>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600, mb: 0.5 }}>
                              Education
                            </Typography>
                            <Typography level="body-sm">{getEducationLabel(devoteeProfile.member.education)}</Typography>
                          </Box>
                          <Box>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600, mb: 0.5 }}>
                              Initiated
                            </Typography>
                            <Typography level="body-sm">{getInitiatedWithLabel(devoteeProfile.member.initiatedWith)}</Typography>
                          </Box>
                          <Box>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600, mb: 0.5 }}>
                              Spiritual master
                            </Typography>
                            <Typography level="body-sm">
                              {getSpiritualMasterName(devoteeProfile.member.spiritualMasterId, masters)}
                            </Typography>
                          </Box>
                          {devoteeProfile.member.about ? (
                            <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                              <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600, mb: 0.5 }}>
                                About
                              </Typography>
                              <Typography level="body-sm">{devoteeProfile.member.about}</Typography>
                            </Box>
                          ) : null}
                          <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' }, pt: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600, mb: 1 }}>
                              Record created
                            </Typography>
                            <Typography level="body-sm">
                              {devoteeProfile.member.createdAt ? formatISTDate(devoteeProfile.member.createdAt) : '—'}
                            </Typography>
                          </Box>
                          <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600, mb: 0.5 }}>
                              Last updated
                            </Typography>
                            <Typography level="body-sm">
                              {devoteeProfile.member.updatedAt ? formatISTDate(devoteeProfile.member.updatedAt) : '—'}
                            </Typography>
                          </Box>
                        </Box>
                      </Stack>
                    </TabPanel>

                    {/* Contact Tab */}
                    <TabPanel value={1} sx={{ p: 0, pt: 2 }}>
                      <Stack spacing={1.5}>
                        {devoteeProfile.contacts && devoteeProfile.contacts.length > 0 ? (
                          <Stack spacing={1} sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 'lg', border: '1px solid', borderColor: 'divider', bgcolor: 'background.level1' }}>
                            {devoteeProfile.contacts.map((contact: any, idx: number) => (
                              <Stack key={idx} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                <Stack sx={{ flex: 1 }}>
                                  <Typography level="body-sm" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    {contact.type}
                                  </Typography>
                                  <Typography level="body-sm" sx={{ color: 'text.secondary', opacity: 0.9 }}>
                                    {contact.value}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" spacing={0.5}>
                                  {contact.isPrimary && (
                                    <Chip size="sm" variant="soft" color="primary">Primary</Chip>
                                  )}
                                  <Chip
                                    size="sm"
                                    variant="soft"
                                    color={contact.verifiedAt ? 'success' : 'neutral'}
                                    startDecorator={contact.verifiedAt ? <VerifiedRoundedIcon fontSize="small" /> : undefined}
                                  >
                                    {contact.verifiedAt ? 'Verified' : 'Unverified'}
                                  </Chip>
                                </Stack>
                              </Stack>
                            ))}
                          </Stack>
                        ) : (
                          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                            No contact methods recorded yet.
                          </Typography>
                        )}
                        <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 'lg', border: '1px solid', borderColor: 'divider', bgcolor: 'background.level1' }}>
                          <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600, mb: 0.5 }}>
                            WhatsApp opt-in
                          </Typography>
                          <Typography level="body-sm">{devoteeProfile.member.whatsappOptIn ? 'Yes' : 'No'}</Typography>
                          {(devoteeProfile.member.emergencyContactName || devoteeProfile.member.emergencyContactPhone) ? (
                            <Stack spacing={1} sx={{ mt: 1 }}>
                              <Divider />
                              <Box>
                                <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600, mb: 0.5 }}>
                                  Emergency contact name
                                </Typography>
                                <Typography level="body-sm">{devoteeProfile.member.emergencyContactName || '—'}</Typography>
                              </Box>
                              <Box>
                                <Typography level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 600, mb: 0.5 }}>
                                  Emergency contact phone
                                </Typography>
                                <Typography level="body-sm">{devoteeProfile.member.emergencyContactPhone || '—'}</Typography>
                              </Box>
                            </Stack>
                          ) : null}
                        </Box>
                      </Stack>
                    </TabPanel>

                    {/* Address Tab */}
                    <TabPanel value={2} sx={{ p: 0, pt: 2 }}>
                      <Stack spacing={2}>
                        {devoteeProfile.addresses && devoteeProfile.addresses.length > 0 ? (
                          <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 'lg', border: '1px solid', borderColor: 'divider', bgcolor: 'background.level1' }}>
                            {devoteeProfile.addresses.map((address: any, idx: number) => (
                              <Box key={idx}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                  <Typography level="body-sm" sx={{ fontWeight: 700 }}>
                                    {address.kind}
                                  </Typography>
                                </Stack>
                                <Typography level="body-sm" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                  {address.line1}
                                  {address.line2 && <><br />{address.line2}</>}
                                  {(address.city || address.state || address.postalCode) && (
                                    <>
                                      <br />
                                      {[address.city, address.state, address.postalCode].filter(Boolean).join(', ')}
                                    </>
                                  )}
                                  {address.country && <><br />{address.country}</>}
                                </Typography>
                                {idx < (devoteeProfile.addresses?.length ?? 0) - 1 && <Divider sx={{ my: 1.5 }} />}
                              </Box>
                            ))}
                          </Box>
                        ) : devoteeProfile.member && (devoteeProfile.member.address || devoteeProfile.member.city) ? (
                          <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 'lg', border: '1px solid', borderColor: 'divider', bgcolor: 'background.level1' }}>
                            <Typography level="body-sm" sx={{ fontWeight: 700, mb: 1 }}>
                              Current address
                            </Typography>
                            <Typography level="body-sm" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                              {devoteeProfile.member.address || 'No address provided'}
                              {(devoteeProfile.member.city || devoteeProfile.member.state || devoteeProfile.member.postalCode) && (
                                <>
                                  <br />
                                  {[devoteeProfile.member.city, devoteeProfile.member.state, devoteeProfile.member.postalCode].filter(Boolean).join(', ')}
                                </>
                              )}
                              {devoteeProfile.member.country && <><br />{devoteeProfile.member.country}</>}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                            No addresses have been added yet.
                          </Typography>
                        )}
                      </Stack>
                    </TabPanel>

                    {/* Photos Tab */}
                    <TabPanel value={3} sx={{ p: 0, pt: 2 }}>
                      <Stack spacing={2}>
                        {devoteeProfile.photos && devoteeProfile.photos.length > 0 ? (
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 1.5 }}>
                            {devoteeProfile.photos.map((photo: any) => (
                              <Box
                                key={photo.id}
                                sx={{
                                  aspectRatio: '1',
                                  borderRadius: 'md',
                                  overflow: 'hidden',
                                  bgcolor: 'background.level2',
                                }}
                              >
                                <img
                                  src={photo.url || ''}
                                  alt={photo.caption || 'Photo'}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                            No photos uploaded yet.
                          </Typography>
                        )}
                      </Stack>
                    </TabPanel>

                    {/* Family Tab */}
                    <TabPanel value={4} sx={{ p: 0, pt: 2 }}>
                      <Stack spacing={1.5}>
                        {devoteeProfile.family && devoteeProfile.family.length > 0 ? (
                          devoteeProfile.family
                            .filter((rel: CongregationFamilyRelation) => rel.status === 'CONFIRMED')
                            .map((relation: CongregationFamilyRelation) => (
                              <Stack
                                key={relation.id}
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                                sx={{
                                  p: 1.5,
                                  borderRadius: 'md',
                                  bgcolor: 'background.level1',
                                }}
                              >
                                <Avatar size="sm">
                                  {relation.relatedMemberId}
                                </Avatar>
                                <Stack spacing={0.25} sx={{ flex: 1 }}>
                                  <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                    Member #{relation.relatedMemberId}
                                  </Typography>
                                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                    {relation.relation.replace(/_/g, ' ').toLowerCase()}
                                  </Typography>
                                  {relation.sinceDate ? (
                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                      Since {formatISTDate(relation.sinceDate)}
                                    </Typography>
                                  ) : null}
                                </Stack>
                                <Chip size="sm" variant="soft" color="success">
                                  {relation.status}
                                </Chip>
                              </Stack>
                            ))
                        ) : (
                          <Typography level="body-sm" sx={{ color: 'text.tertiary', textAlign: 'center', py: 3 }}>
                            No family members added yet.
                          </Typography>
                        )}
                      </Stack>
                    </TabPanel>
                  </Tabs>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Box>
      </Stack>

      {devoteeProfile && (
        <EditPersonalDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          member={devoteeProfile.member}
          onSave={handleSavePersonal}
        />
      )}
    </Box>
  );
};

export default AdminDevoteesPage;
